import { useEffect, useState } from 'react';
import { FileText, Filter, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, DocumentSource } from '../lib/admin-api';
import { DataTable } from '../components/admin/DataTable';

const typeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'policy', label: 'Policy' },
  { value: 'handbook', label: 'Handbook' },
  { value: 'faq', label: 'FAQ' },
  { value: 'help_article', label: 'Help Article' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'product_doc', label: 'Product Doc' },
  { value: 'training', label: 'Training' },
];

export function DocumentsPage() {
  const { profile } = useAuth();
  const [documents, setDocuments] = useState<DocumentSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    async function fetchData() {
      if (!profile?.organization_id) return;

      setLoading(true);
      const data = await adminApi.getDocuments(profile.organization_id);
      setDocuments(data);
      setLoading(false);
    }

    fetchData();
  }, [profile?.organization_id]);

  const filteredDocuments = typeFilter === 'all'
    ? documents
    : documents.filter((d) => d.source_type === typeFilter);

  const getTypeBadge = (type: string) => {
    const styles: Record<string, string> = {
      policy: 'bg-info text-info-foreground',
      handbook: 'bg-info text-info-foreground',
      faq: 'bg-success text-success-foreground',
      help_article: 'bg-warning text-warning-foreground',
      procedure: 'bg-primary/15 text-link',
      product_doc: 'bg-pink-100 text-pink-800',
      training: 'bg-warning text-warning-foreground',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[type] || 'bg-muted text-foreground'}`}>
        {type.replace('_', ' ')}
      </span>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-foreground" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-warning-foreground" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-error-text" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getAccessBadge = (level: string) => {
    const styles: Record<string, string> = {
      public: 'bg-success text-success-foreground',
      organization: 'bg-info text-info-foreground',
      department: 'bg-warning text-warning-foreground',
      employee: 'bg-warning text-warning-foreground',
      hr_only: 'bg-destructive/10 text-error-text',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[level] || 'bg-muted text-foreground'}`}>
        {level.replace('_', ' ')}
      </span>
    );
  };

  const columns = [
    {
      key: 'title',
      header: 'Document',
      render: (doc: DocumentSource) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium text-foreground">{doc.title}</p>
            {doc.description && (
              <p className="text-sm text-muted-foreground truncate max-w-xs">
                {doc.description}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'source_type',
      header: 'Type',
      render: (doc: DocumentSource) => getTypeBadge(doc.source_type),
    },
    {
      key: 'access_level',
      header: 'Access',
      render: (doc: DocumentSource) => getAccessBadge(doc.access_level),
    },
    {
      key: 'processing_status',
      header: 'Status',
      render: (doc: DocumentSource) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(doc.processing_status)}
          <span className="text-sm text-muted-foreground capitalize">
            {doc.processing_status}
          </span>
        </div>
      ),
    },
    {
      key: 'updated_at',
      header: 'Updated',
      render: (doc: DocumentSource) => (
        <span className="text-muted-foreground">
          {new Date(doc.updated_at).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Documents</h1>
          <p className="text-muted-foreground mt-1">Knowledge base for AI responses</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-card p-4 rounded-lg border border-border">
        <Filter className="w-5 h-5 text-muted-foreground" />
        <div className="flex items-center gap-2">
          <label htmlFor="documentspage-1" className="text-sm font-medium text-foreground">Type:</label>
          <select id="documentspage-1"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
          >
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <span className="text-sm text-muted-foreground ml-auto">
          {filteredDocuments.length} document{filteredDocuments.length === 1 ? '' : 's'}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={filteredDocuments}
        loading={loading}
        emptyMessage="No documents found. Upload documents to enable AI-powered responses."
        keyExtractor={(doc) => doc.id}
      />

      <div className="bg-primary/15 border border-primary rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-link mt-0.5" />
          <div>
            <p className="text-sm font-medium text-link">About Documents</p>
            <p className="text-sm text-link mt-1">
              Documents are automatically processed and chunked for RAG-based AI responses.
              The AI will cite relevant documents when answering customer questions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
